import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { SOCIAL_KIND_LIMIT_CEILINGS, SOCIAL_KIND_LIMIT_DEFAULTS, SocialReactionMode, SocialReplyLang, } from '@wit-support/admin-contracts';
import { Badge, Button, Card, ConfirmDialog, Dialog, DialogContent, DialogDescription, DialogTitle, EmptyState, InlineNotice, SectionCard, Skeleton, toast, } from '@wit-support/ui';
import { CONTENT_NETWORK_LABEL_RU, NetworkChip, Time, actorLabelRu } from '@wit-support/ui-domain';
import { Actions, CancelButton, SubmitButton, ZodField, ZodForm } from '@wit-support/ui-forms';
import { RefreshCw, SlidersHorizontal } from 'lucide-react';
import { listPolicies, listSettings, runPoll, updateSettings } from '../api.js';
import { SOCIAL_LIMIT_KIND_LABEL_RU, SOCIAL_LIMIT_KIND_ORDER, SOCIAL_MODE_HINT_RU, SOCIAL_MODE_LABEL_RU, policyLabel, settingsSavedText, } from '../outcome-text.js';
import { AccountSafety, GlobalStopControl, pauseActive } from '../safety.js';
import { ENGAGE_V1_POLICY, NOOP_POLICY, policyDescription } from '../policy-text.js';
import { ShadowSummaryBlock, useShadowSummary } from '../shadow.js';
import { shadowModeDescription } from '../shadow-text.js';
import { netText, networkLimitsHint, networkSafetyOf } from '../network-text.js';
import { POLICIES_KEY, SETTINGS_KEY, SOCIAL_KEY, SocialErrorState, SocialScreen, describeSocialError, socialCodeText, submitting, useSocialConnections, } from '../shared.js';
export const AUTO_DELETE_WARNING = 'Удаление необратимо. Даже в режиме «Авто» алгоритм удаляет комментарии сам, только если это включено.';
/** Пояснение в форме супервайзера: что он может, а что — только владелец. */
export const SUPERVISOR_SETTINGS_NOTE = 'Выключить «Авто» или «Тень» и автоудаление, снизить лимиты, выбрать алгоритм в режиме «Вручную» можно. Включить «Авто» или «Тень» и автоудаление, поднять лимиты, сменить алгоритм в «Авто» или «Тени» — только владелец.';
/** «Тень» без настоящего алгоритма ничего не решает. */
export const SHADOW_NOOP_WARNING = 'Режим «Тень» с алгоритмом «Нет — только вручную» ничего не покажет: выберите «Алгоритм v1».';
/** Язык ответов по умолчанию — подписи и пояснение (AN-1 §6.9). */
export const REPLY_LANG_LABEL_RU = {
    en: 'английский',
    pt: 'португальский',
    es: 'испанский',
};
export const DEFAULT_LANG_HINT = 'На этом языке алгоритм отвечает на комментарии без слов (сердечки, отметки друзей), если язык поста и аккаунта понять не удалось.';
/**
 * У Facebook отметки друзей без текста — половина комментариев, и тексты там
 * на многих языках (FB-SPEC §0.7, M6): на отметку алгоритм отвечает, только
 * если язык ролика понятен; язык по умолчанию для отметок не берёт.
 */
export const FB_DEFAULT_LANG_HINT = 'На Facebook на отметки друзей без слов алгоритм отвечает, только если язык ролика понятен (португальский, испанский или английский). Язык по умолчанию для отметок не используется: иначе ответ ушёл бы людям, которые говорят на другом языке.';
/** Подсказка языка по умолчанию — по сети пары. */
export function defaultLangHint(network) {
    return network === 'facebook'
        ? `${DEFAULT_LANG_HINT} ${FB_DEFAULT_LANG_HINT}`
        : DEFAULT_LANG_HINT;
}
/** Подсказка у «Алгоритм» для супервайзера на аккаунте в «Авто». */
export const SUPERVISOR_POLICY_AUTO_NOTE = 'В режимах «Авто» и «Тень» включить или сменить алгоритм может только владелец. Выключить («Нет — только вручную») можно.';
export const POLL_STARTED_TEXT = 'Опрос запущен — новые комментарии появятся через пару минут';
export const POLL_NOT_STARTED_TEXT = 'Опрос не запущен — попробуйте позже';
/**
 * `started:false` — `reason` движка машинная строка: знакомый код — своей
 * фразой, остальное — общей, без кода в тексте.
 */
export function pollNotStartedText(reason) {
    return (reason !== null ? socialCodeText(reason) : null) ?? POLL_NOT_STARTED_TEXT;
}
const LIMIT_LABEL_RU = {
    perRun: 'Действий за один прогон',
    minGapSec: 'Пауза между действиями, секунд',
    perDay: 'Действий в сутки',
    likesPer5s: 'Сердечек за 5 секунд',
};
const int = (label, min, max) => z
    .number({ message: 'Укажите число' })
    .int('Целое число')
    .min(min, `Не меньше ${min}`)
    .max(max, `Не больше ${max}`)
    .meta({ label });
const kindField = (kind, win) => `${kind}_${win}`;
/**
 * Потолок вида: прислал движок для пары — его; иначе потолок профиля сети
 * пары (`safetyByNetwork[network].caps`, у Facebook ниже); иначе копия
 * констант движка из контракта (Instagram).
 */
export function kindCeiling(s, kind, net) {
    return s.kindLimitCeilings?.[kind] ?? net?.caps?.[kind] ?? SOCIAL_KIND_LIMIT_CEILINGS[kind];
}
/** Действующее значение вида: из настроек, иначе умолчание сети пары, иначе умолчание движка. */
export function kindValue(s, kind, net) {
    return s.kindLimits?.[kind] ?? net?.defaults?.[kind] ?? SOCIAL_KIND_LIMIT_DEFAULTS[kind];
}
/** Окна лимитов, которые правятся в форме: суточное — только у видов, где оно есть. */
function kindWindows(s, net) {
    return SOCIAL_LIMIT_KIND_ORDER.flatMap((kind) => {
        const wins = kindCeiling(s, kind, net).per24h !== null ? ['perHour', 'per24h'] : ['perHour'];
        return wins.map((win) => ({ kind, win }));
    });
}
const kindFieldLabel = (kind, win, max, lowerOnly = false) => `${SOCIAL_LIMIT_KIND_LABEL_RU[kind]}: ${win === 'perHour' ? 'в час' : 'за 24 часа'} (${lowerOnly ? `можно только снизить, сейчас ${max}` : `не больше ${max}`})`;
/**
 * Верхняя граница поля лимита. Владелец — потолок движка. Супервайзер может
 * только СНИЗИТЬ (RULINGS-SAFETY-2): граница — текущее значение; у окна без
 * значения (суточного лимита не было) задать любое до потолка — тоже
 * ужесточение.
 */
export function kindFieldMax(s, kind, win, isOwner, net) {
    const ceiling = kindCeiling(s, kind, net)[win] ?? 1;
    if (isOwner)
        return ceiling;
    const cur = s.kindLimits?.[kind]?.[win] ?? null;
    return cur === null ? ceiling : Math.min(cur, ceiling);
}
/**
 * Форма настроек аккаунта × сети. Лимиты — плоско (в теле запроса они
 * вложены в `limits` / `kindLimits`, см. {@link settingsDiff}). Список
 * алгоритмов — из `/policies`; текущий алгоритм остаётся в списке, даже если
 * движок его уже не знает (иначе форму нельзя было бы сохранить).
 *
 * `isOwner: false` (супервайзер, RULINGS-SAFETY-2) — только выключающее:
 * «Авто» в списке режимов, только если он уже включён (выключить можно);
 * автоудаление — только если включено (выключить можно); лимиты по видам —
 * не выше текущих (снизить можно); старых лимитов нет. Сервер проверяет это
 * сам. Лимиты по видам — только если движок их отдаёт.
 */
export function settingsFormSchema(policies, current, opts = {}) {
    const isOwner = opts.isOwner ?? true;
    const net = opts.netSafety;
    // Супервайзер на аккаунте в «Авто»: алгоритм — только выключить (как режим,
    // S-POL); остальные варианты сервер всё равно отвергнет 403.
    // …и в «Тени» (RV-SHADOW L3): там алгоритм включает теневые решения
    const policyLocked = !isOwner && (opts.settings?.mode === 'auto' || opts.settings?.mode === 'shadow');
    const names = [
        ...new Set([...(policyLocked ? [NOOP_POLICY] : policies.map((p) => p.name)), current]),
    ];
    const describe = (n) => policyLabel(n, policies.find((p) => p.name === n)?.description);
    // «Авто» и «Тень» включает только владелец; уже включённый режим остаётся в
    // списке и у супервайзера (иначе форму нельзя было бы сохранить).
    const modes = SocialReactionMode.options.filter((m) => isOwner || (m !== 'auto' && m !== 'shadow') || opts.settings?.mode === m);
    const base = z.object({
        mode: z.enum(modes).meta({
            label: 'Режим',
            ...(isOwner ? {} : { description: 'Включить «Авто» или «Тень» может только владелец.' }),
            options: modes.map((m) => ({ value: m, label: SOCIAL_MODE_HINT_RU[m] })),
        }),
        policy: z.enum(names).meta({
            label: 'Алгоритм',
            description: policyLocked
                ? SUPERVISOR_POLICY_AUTO_NOTE
                : 'Решает, что сделать или предложить по каждому комментарию.',
            options: names.map((n) => ({ value: n, label: describe(n) })),
        }),
        pollMediaCount: int('Сколько последних постов проверять', 1, 50),
        // старый движок `defaultLang` не отдаёт — поля нет
        ...(opts.settings?.defaultLang != null
            ? {
                defaultLang: SocialReplyLang.meta({
                    label: 'Язык ответов по умолчанию',
                    description: defaultLangHint(opts.settings.network),
                    options: SocialReplyLang.options.map((l) => ({
                        value: l,
                        label: REPLY_LANG_LABEL_RU[l],
                    })),
                }),
            }
            : {}),
    });
    const kinds = {};
    if (opts.settings?.kindLimits) {
        for (const { kind, win } of kindWindows(opts.settings, net)) {
            const max = kindFieldMax(opts.settings, kind, win, isOwner, net);
            const lowerOnly = !isOwner && (opts.settings.kindLimits[kind]?.[win] ?? null) !== null;
            kinds[kindField(kind, win)] = int(kindFieldLabel(kind, win, max, lowerOnly), 1, max);
        }
    }
    if (!isOwner) {
        return base.extend({
            ...(opts.settings?.autoDelete
                ? {
                    autoDelete: z.boolean().meta({
                        label: 'Удалять автоматически',
                        description: 'Выключить можно; включить снова — только владелец.',
                    }),
                }
                : {}),
            ...kinds,
        });
    }
    return base.extend({
        autoDelete: z.boolean().meta({
            label: 'Удалять автоматически',
            description: AUTO_DELETE_WARNING,
        }),
        perRun: int(LIMIT_LABEL_RU.perRun, 1, 50),
        minGapSec: int(LIMIT_LABEL_RU.minGapSec, 1, 60),
        perDay: int(LIMIT_LABEL_RU.perDay, 1, 1000),
        likesPer5s: int(LIMIT_LABEL_RU.likesPer5s, 1, 10),
        ...kinds,
    });
}
export function settingsDefaults(s, net) {
    const kinds = {};
    if (s.kindLimits) {
        for (const { kind, win } of kindWindows(s, net)) {
            const v = kindValue(s, kind, net)[win];
            if (v !== null)
                kinds[kindField(kind, win)] = v;
        }
    }
    return {
        mode: s.mode,
        policy: s.policy,
        autoDelete: s.autoDelete,
        pollMediaCount: s.pollMediaCount,
        ...(s.defaultLang !== null ? { defaultLang: s.defaultLang } : {}),
        ...s.limits,
        ...kinds,
    };
}
/** Тело PUT — только изменённые поля; null — ничего не изменилось. Поля, которых нет в форме, не трогаем. */
export function settingsDiff(current, next, net) {
    const body = {};
    if (next.mode !== current.mode)
        body.mode = next.mode;
    if (next.policy !== current.policy)
        body.policy = next.policy;
    if (next.autoDelete !== undefined && next.autoDelete !== current.autoDelete) {
        body.autoDelete = next.autoDelete;
    }
    if (next.pollMediaCount !== current.pollMediaCount)
        body.pollMediaCount = next.pollMediaCount;
    if (current.defaultLang !== null &&
        next.defaultLang !== undefined &&
        next.defaultLang !== current.defaultLang) {
        body.defaultLang = next.defaultLang;
    }
    const limits = {};
    for (const k of ['perRun', 'minGapSec', 'perDay', 'likesPer5s']) {
        const v = next[k];
        if (v !== undefined && v !== current.limits[k])
            limits[k] = v;
    }
    if (Object.keys(limits).length > 0)
        body.limits = limits;
    if (current.kindLimits) {
        const kindLimits = {};
        for (const { kind, win } of kindWindows(current, net)) {
            const v = next[kindField(kind, win)];
            if (typeof v !== 'number' || v === kindValue(current, kind, net)[win])
                continue;
            (kindLimits[kind] ??= {})[win] = v;
        }
        if (Object.keys(kindLimits).length > 0) {
            body.kindLimits = kindLimits;
        }
    }
    return Object.keys(body).length > 0 ? body : null;
}
/**
 * Изменения, которые требуют подтверждения: действия без человека.
 * `currentMode` — режим до правки: включить алгоритм на аккаунте, который
 * уже в «Авто», — тоже действия без человека.
 */
export function riskyChange(body, currentMode) {
    if (body.autoDelete === true) {
        return 'Алгоритм сможет удалять комментарии сам, без подтверждения. Удаление необратимо.';
    }
    if (body.mode === 'auto') {
        return 'Действия, которые предложит алгоритм, будут выполняться сразу, без подтверждения человеком.';
    }
    if (body.policy !== undefined &&
        body.policy !== NOOP_POLICY &&
        (body.mode ?? currentMode) === 'auto') {
        return 'Аккаунт в режиме «Авто»: алгоритм сразу начнёт ставить сердечки, отвечать и скрывать спам сам, без подтверждения человеком. Вопросы, негатив и оскорбления он всё равно отдаст человеку.';
    }
    return null;
}
/** «Настройки реагирования» — режим, алгоритм и лимиты на каждый аккаунт × сеть. */
export function SocialSettingsPage({ canManage, canManageConnections = false, }) {
    const status = useSocialConnections();
    const pollMut = useMutation({
        mutationFn: () => runPoll({}),
        onSuccess: (r) => {
            if (r.started)
                toast.success(POLL_STARTED_TEXT);
            else
                toast.info(pollNotStartedText(r.reason));
        },
        onError: (err) => toast.error(describeSocialError(err, 'write')),
    });
    return (_jsx(SocialScreen, { title: "\u041D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438 \u0440\u0435\u0430\u0433\u0438\u0440\u043E\u0432\u0430\u043D\u0438\u044F", description: "\u041A\u0430\u043A \u0434\u0432\u0438\u0436\u043E\u043A \u0440\u0435\u0430\u0433\u0438\u0440\u0443\u0435\u0442 \u043D\u0430 \u043A\u043E\u043C\u043C\u0435\u043D\u0442\u0430\u0440\u0438\u0438 \u043A\u0430\u0436\u0434\u043E\u0433\u043E \u0430\u043A\u043A\u0430\u0443\u043D\u0442\u0430: \u0442\u043E\u043B\u044C\u043A\u043E \u0436\u0443\u0440\u043D\u0430\u043B, \u043F\u0440\u0435\u0434\u043B\u043E\u0436\u0435\u043D\u0438\u044F \u043D\u0430 \u043F\u043E\u0434\u0442\u0432\u0435\u0440\u0436\u0434\u0435\u043D\u0438\u0435 \u0438\u043B\u0438 \u0430\u0432\u0442\u043E\u043C\u0430\u0442\u0438\u0447\u0435\u0441\u043A\u0438.", actions: _jsxs(_Fragment, { children: [_jsx(GlobalStopControl, { globalStop: status.data?.globalStop ?? null, canManage: canManage, canManageConnections: canManageConnections }), canManage ? (_jsxs(Button, { type: "button", variant: "secondary", disabled: pollMut.isPending, onClick: () => pollMut.mutate(), children: [_jsx(RefreshCw, { className: "mr-1 h-4 w-4", "aria-hidden": "true" }), pollMut.isPending ? 'Запускаем…' : 'Опросить сейчас'] })) : null] }), children: (status) => (_jsx(SettingsBody, { status: status, canManage: canManage, canManageConnections: canManageConnections })) }));
}
function SettingsBody({ status, canManage, canManageConnections, }) {
    const qc = useQueryClient();
    const settings = useQuery({ queryKey: SETTINGS_KEY, queryFn: listSettings });
    const policies = useQuery({ queryKey: POLICIES_KEY, queryFn: listPolicies });
    const shadow = useShadowSummary();
    const [editing, setEditing] = useState(null);
    const [confirm, setConfirm] = useState(null);
    const mut = useMutation({
        mutationFn: (v) => updateSettings({ account: v.item.account, network: v.item.network }, v.body),
        onSuccess: (r) => {
            // RV-SHADOW M1: при смене режима на «Тень»/«Выключено» движок снимает очередь алгоритма
            toast.success(settingsSavedText(r.rejectedPolicyActions));
            setEditing(null);
            void qc.invalidateQueries({ queryKey: SOCIAL_KEY });
        },
        onError: (err, v) => toast.error(describeSocialError(err, 'write', v.item.network)),
    });
    if (settings.isError)
        return _jsx(SocialErrorState, { error: settings.error });
    // Профили защиты по сетям: из «Настроек», иначе из «Подключений» (FB-SPEC §4.2)
    const byNetwork = settings.data?.safetyByNetwork ?? status.safetyByNetwork;
    if (!settings.data)
        return _jsx(Skeleton, { className: "h-48 w-full" });
    // Сети без адаптера не настраиваются: реагировать там пока нечем.
    const unsupported = new Set(status.items.filter((c) => !c.supported).map((c) => `${c.account}:${c.network}`));
    const items = settings.data.items.filter((s) => !unsupported.has(`${s.account}:${s.network}`));
    const policyList = policies.data?.items ?? [];
    if (items.length === 0) {
        return (_jsx(Card, { children: _jsx(EmptyState, { icon: SlidersHorizontal, title: "\u041D\u0430\u0441\u0442\u0440\u0430\u0438\u0432\u0430\u0442\u044C \u043F\u043E\u043A\u0430 \u043D\u0435\u0447\u0435\u0433\u043E", description: "\u0412 \u0440\u0430\u0441\u043F\u0438\u0441\u0430\u043D\u0438\u0438 \u0434\u0432\u0438\u0436\u043A\u0430 \u043D\u0435\u0442 \u0430\u043A\u043A\u0430\u0443\u043D\u0442\u043E\u0432 \u0432 \u0441\u0435\u0442\u044F\u0445, \u0433\u0434\u0435 \u0440\u0435\u0430\u0433\u0438\u0440\u043E\u0432\u0430\u043D\u0438\u0435 \u0443\u0436\u0435 \u0441\u0434\u0435\u043B\u0430\u043D\u043E." }) }));
    }
    return (_jsxs(_Fragment, { children: [_jsx(ShadowSummaryBlock, { status: status, state: shadow }), _jsx(PolicyOverview, { settings: items, policies: policyList }), policies.isError ? (_jsxs(InlineNotice, { variant: "warning", bordered: true, children: ["\u0421\u043F\u0438\u0441\u043E\u043A \u0430\u043B\u0433\u043E\u0440\u0438\u0442\u043C\u043E\u0432 \u043D\u0435 \u0437\u0430\u0433\u0440\u0443\u0437\u0438\u043B\u0441\u044F: ", describeSocialError(policies.error)] })) : null, items.map((s) => (_jsx(SettingsCard, { s: s, cell: status.items.find((c) => c.account === s.account && c.network === s.network), items: status.items, netSafety: networkSafetyOf(byNetwork, s.network), policies: policyList, canManage: canManage, canManageConnections: canManageConnections, onEdit: () => setEditing(s) }, `${s.account}:${s.network}`))), editing ? (_jsx(Dialog, { open: true, onOpenChange: (v) => (!v ? setEditing(null) : null), children: _jsxs(DialogContent, { size: "lg", children: [_jsxs(DialogTitle, { children: [editing.account, " \u00B7 ", CONTENT_NETWORK_LABEL_RU[editing.network], ": \u0440\u0435\u0430\u0433\u0438\u0440\u043E\u0432\u0430\u043D\u0438\u0435"] }), _jsx(DialogDescription, { children: "\u0414\u0435\u0439\u0441\u0442\u0432\u0443\u0435\u0442 \u0441\u043E \u0441\u043B\u0435\u0434\u0443\u044E\u0449\u0435\u0433\u043E \u043E\u043F\u0440\u043E\u0441\u0430 \u043A\u043E\u043C\u043C\u0435\u043D\u0442\u0430\u0440\u0438\u0435\u0432." }), _jsx(SettingsForm, { s: editing, netSafety: networkSafetyOf(byNetwork, editing.network), isOwner: canManageConnections, policies: policyList, onCancel: () => setEditing(null), onSubmit: (body) => {
                                const risk = riskyChange(body, editing.mode);
                                if (risk) {
                                    setConfirm({ item: editing, body, text: risk });
                                    return undefined;
                                }
                                return submitting(mut.mutateAsync({ item: editing, body }));
                            } })] }) })) : null, _jsx(ConfirmDialog, { open: confirm !== null, onOpenChange: (v) => (!v ? setConfirm(null) : null), title: "\u0412\u043A\u043B\u044E\u0447\u0438\u0442\u044C \u0434\u0435\u0439\u0441\u0442\u0432\u0438\u044F \u0431\u0435\u0437 \u043F\u043E\u0434\u0442\u0432\u0435\u0440\u0436\u0434\u0435\u043D\u0438\u044F?", description: confirm?.text ?? '', cancelLabel: "\u041E\u0442\u043C\u0435\u043D\u0430", actionLabel: "\u0412\u043A\u043B\u044E\u0447\u0438\u0442\u044C", actionStyle: "danger", onConfirm: () => {
                    if (confirm)
                        mut.mutate({ item: confirm.item, body: confirm.body });
                    setConfirm(null);
                } })] }));
}
function SettingsForm({ s, netSafety, isOwner, policies, onCancel, onSubmit, }) {
    const [showLimits, setShowLimits] = useState(false);
    return (_jsxs(ZodForm, { schema: settingsFormSchema(policies, s.policy, {
            isOwner,
            settings: s,
            ...(netSafety ? { netSafety } : {}),
        }), defaultValues: settingsDefaults(s, netSafety), onSubmit: (values) => {
            const body = settingsDiff(s, values, netSafety);
            if (body === null) {
                toast.info('Ничего не изменилось');
                onCancel();
                return;
            }
            return onSubmit(body);
        }, children: [_jsx(ZodField, { name: "mode" }), isOwner || s.mode === 'shadow' ? _jsx(ShadowModeHelp, { network: s.network }) : null, _jsx(ZodField, { name: "policy" }), _jsx(PolicyHelp, { policies: policies }), s.defaultLang !== null ? _jsx(ZodField, { name: "defaultLang" }) : null, isOwner || s.autoDelete ? _jsx(ZodField, { name: "autoDelete" }) : null, _jsx(ZodField, { name: "pollMediaCount" }), isOwner || s.kindLimits ? (_jsxs(_Fragment, { children: [_jsx(Button, { type: "button", variant: "ghost", size: "sm", className: "self-start", "aria-expanded": showLimits, onClick: () => setShowLimits((v) => !v), children: showLimits ? 'Скрыть лимиты' : 'Показать лимиты' }), showLimits ? (_jsxs(_Fragment, { children: [networkLimitsHint(s.network, netSafety) ? (_jsx(InlineNotice, { variant: "muted", as: "p", children: networkLimitsHint(s.network, netSafety) })) : null, s.kindLimits
                                ? kindWindows(s, netSafety).map(({ kind, win }) => (_jsx(ZodField, { name: kindField(kind, win) }, kindField(kind, win))))
                                : null, isOwner ? (_jsxs(_Fragment, { children: [_jsx(ZodField, { name: "perRun" }), _jsx(ZodField, { name: "minGapSec" }), _jsx(ZodField, { name: "perDay" }), _jsx(ZodField, { name: "likesPer5s" })] })) : null] })) : null] })) : null, isOwner ? null : (_jsx(InlineNotice, { variant: "muted", as: "p", children: SUPERVISOR_SETTINGS_NOTE })), _jsxs(Actions, { children: [_jsx(CancelButton, { onClick: onCancel }), _jsx(SubmitButton, { pendingLabel: "\u0421\u043E\u0445\u0440\u0430\u043D\u044F\u0435\u043C\u2026", children: "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C" })] })] }, `${s.account}:${s.network}`));
}
/** Что такое «Тень» — под выбором режима, простыми словами. */
function ShadowModeHelp({ network }) {
    return (_jsx(InlineNotice, { variant: "muted", as: "div", children: _jsxs("div", { className: "flex flex-col gap-0.5 text-sm", "aria-label": "\u0427\u0442\u043E \u0442\u0430\u043A\u043E\u0435 \u0440\u0435\u0436\u0438\u043C \u00AB\u0422\u0435\u043D\u044C\u00BB", children: [_jsx("span", { className: "font-medium", children: SOCIAL_MODE_HINT_RU.shadow }), _jsx("ul", { className: "list-disc pl-5", children: shadowModeDescription(network).map((l) => (_jsx("li", { children: l }, l))) })] }) }));
}
/**
 * «Что делает алгоритм v1» — один раз над карточками аккаунтов, если он
 * выбран хоть где-то или есть в списке движка (чтобы владелец знал, что
 * включает). Остальные алгоритмы описаны в форме.
 */
function PolicyOverview({ settings, policies, }) {
    const known = settings.some((x) => x.policy === ENGAGE_V1_POLICY) ||
        policies.some((p) => p.name === ENGAGE_V1_POLICY);
    if (!known)
        return null;
    const using = settings.filter((x) => x.policy === ENGAGE_V1_POLICY).length;
    return (_jsx(SectionCard, { title: "\u0427\u0442\u043E \u0434\u0435\u043B\u0430\u0435\u0442 \u00AB\u0410\u043B\u0433\u043E\u0440\u0438\u0442\u043C v1\u00BB", hint: using > 0
            ? `Выбран у ${using} из ${settings.length} аккаунтов`
            : 'Пока ни у одного аккаунта не выбран — «Изменить» → «Алгоритм»', children: _jsx("ul", { className: "list-disc pl-5 text-sm", "aria-label": "\u0427\u0442\u043E \u0434\u0435\u043B\u0430\u0435\u0442 \u0430\u043B\u0433\u043E\u0440\u0438\u0442\u043C v1", children: policyDescription(ENGAGE_V1_POLICY).map((l) => (_jsx("li", { children: l }, l))) }) }));
}
/**
 * Что делает каждый алгоритм — простыми словами, под выбором алгоритма
 * (выбранный в форме виден в поле выше, описания — всех доступных).
 */
function PolicyHelp({ policies }) {
    const names = [...new Set([...policies.map((p) => p.name)])];
    const items = names
        .map((n) => ({
        n,
        lines: policyDescription(n, policies.find((p) => p.name === n)?.description),
    }))
        .filter((x) => x.lines.length > 0);
    if (items.length === 0)
        return null;
    return (_jsx(InlineNotice, { variant: "muted", as: "div", children: _jsx("div", { className: "flex flex-col gap-2 text-sm", "aria-label": "\u0427\u0442\u043E \u0434\u0435\u043B\u0430\u044E\u0442 \u0430\u043B\u0433\u043E\u0440\u0438\u0442\u043C\u044B", children: items.map(({ n, lines }) => (_jsxs("div", { className: "flex flex-col gap-0.5", children: [_jsx("span", { className: "font-medium", children: policyLabel(n, policies.find((p) => p.name === n)?.description) }), _jsx("ul", { className: "list-disc pl-5", children: lines.map((l) => (_jsx("li", { children: l }, l))) })] }, n))) }) }));
}
/** «Ответы: 15 в час, 60 за сутки (потолок 20 и 100)». */
export function kindLimitText(s, kind, net) {
    const v = kindValue(s, kind, net);
    const c = kindCeiling(s, kind, net);
    const value = v.per24h !== null ? `${v.perHour} в час, ${v.per24h} за сутки` : `${v.perHour} в час`;
    const cap = c.per24h !== null ? `${c.perHour} и ${c.per24h}` : `${c.perHour}`;
    return `${SOCIAL_LIMIT_KIND_LABEL_RU[kind]}: ${value} (потолок ${cap})`;
}
const MODE_VARIANT = {
    off: 'neutral',
    manual: 'info',
    shadow: 'accent',
    auto: 'warning',
};
/** Под режимом «Тень» в карточке аккаунта. */
export const SHADOW_CARD_HINT = 'В Instagram ничего не уходит. Что решил бы алгоритм — в «Комментариях» (столбец «Алгоритм сделал бы») и в блоке «Тень» выше.';
/** То же с именем сети пары. */
export const shadowCardHint = (network) => netText(SHADOW_CARD_HINT, network);
function SettingsCard({ s, cell, items, netSafety, policies, canManage, canManageConnections, onEdit, }) {
    const policy = policyLabel(s.policy, policies.find((p) => p.name === s.policy)?.description);
    return (_jsx(SectionCard, { title: _jsxs("span", { className: "flex items-center gap-2", children: [s.account, " ", _jsx(NetworkChip, { network: s.network })] }), hint: s.updatedAt ? (_jsxs(_Fragment, { children: ["\u0418\u0437\u043C\u0435\u043D\u0435\u043D\u043E ", _jsx(Time, { iso: s.updatedAt, preset: "shortNumeric" }), actorLabelRu(s.author) ? ` · ${actorLabelRu(s.author)}` : ''] })) : ('Настройки по умолчанию'), actions: canManage ? (_jsx(Button, { type: "button", variant: "secondary", size: "sm", "aria-label": `Изменить: ${s.account} · ${CONTENT_NETWORK_LABEL_RU[s.network]}`, onClick: onEdit, children: "\u0418\u0437\u043C\u0435\u043D\u0438\u0442\u044C" })) : null, children: _jsxs("dl", { className: "grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[max-content_1fr]", children: [_jsx("dt", { className: "text-[var(--color-text-muted)]", children: "\u0420\u0435\u0436\u0438\u043C" }), _jsxs("dd", { className: "flex flex-wrap items-center gap-2", children: [_jsx(Badge, { variant: MODE_VARIANT[s.mode], children: SOCIAL_MODE_LABEL_RU[s.mode] }), _jsx("span", { className: "text-[var(--color-text-muted)]", children: SOCIAL_MODE_HINT_RU[s.mode] }), s.mode === 'shadow' ? (_jsx("span", { className: "basis-full text-xs text-[var(--color-text-muted)]", children: shadowCardHint(s.network) })) : null, s.mode === 'shadow' && s.policy === NOOP_POLICY ? (_jsx("span", { className: "basis-full text-xs text-[var(--color-state-warning-fg)]", children: SHADOW_NOOP_WARNING })) : null] }), _jsx("dt", { className: "text-[var(--color-text-muted)]", children: "\u0410\u043B\u0433\u043E\u0440\u0438\u0442\u043C" }), _jsx("dd", { children: policy }), s.defaultLang !== null ? (_jsxs(_Fragment, { children: [_jsx("dt", { className: "text-[var(--color-text-muted)]", children: "\u042F\u0437\u044B\u043A \u043E\u0442\u0432\u0435\u0442\u043E\u0432 \u043F\u043E \u0443\u043C\u043E\u043B\u0447\u0430\u043D\u0438\u044E" }), _jsx("dd", { title: defaultLangHint(s.network), children: REPLY_LANG_LABEL_RU[s.defaultLang] })] })) : null, _jsx("dt", { className: "text-[var(--color-text-muted)]", children: "\u0423\u0434\u0430\u043B\u044F\u0442\u044C \u0430\u0432\u0442\u043E\u043C\u0430\u0442\u0438\u0447\u0435\u0441\u043A\u0438" }), _jsx("dd", { children: s.autoDelete ? _jsx(Badge, { variant: "danger", children: "\u0432\u043A\u043B\u044E\u0447\u0435\u043D\u043E" }) : 'выключено' }), _jsx("dt", { className: "text-[var(--color-text-muted)]", children: "\u041F\u0440\u043E\u0432\u0435\u0440\u044F\u0442\u044C \u043F\u043E\u0441\u0442\u043E\u0432" }), _jsxs("dd", { children: [s.pollMediaCount, " \u043F\u043E\u0441\u043B\u0435\u0434\u043D\u0438\u0445"] }), s.kindLimits ? (_jsxs(_Fragment, { children: [_jsx("dt", { className: "text-[var(--color-text-muted)]", children: "\u041B\u0438\u043C\u0438\u0442\u044B \u043F\u043E \u0432\u0438\u0434\u0430\u043C" }), _jsxs("dd", { children: [_jsx("ul", { className: "flex flex-col gap-0.5", "aria-label": "\u041B\u0438\u043C\u0438\u0442\u044B \u043F\u043E \u0432\u0438\u0434\u0430\u043C", children: SOCIAL_LIMIT_KIND_ORDER.map((k) => (_jsx("li", { children: kindLimitText(s, k, netSafety) }, k))) }), networkLimitsHint(s.network, netSafety) ? (_jsx("p", { className: "mt-1 text-xs text-[var(--color-text-muted)]", children: networkLimitsHint(s.network, netSafety) })) : null] })] })) : null, _jsx("dt", { className: "text-[var(--color-text-muted)]", children: "\u041B\u0438\u043C\u0438\u0442\u044B" }), _jsxs("dd", { className: "text-[var(--color-text-muted)]", children: [s.limits.perRun, " \u0437\u0430 \u043F\u0440\u043E\u0433\u043E\u043D \u00B7 \u043F\u0430\u0443\u0437\u0430 ", s.limits.minGapSec, " \u0441 \u00B7 ", s.limits.perDay, " \u0432 \u0441\u0443\u0442\u043A\u0438 \u00B7", ' ', s.limits.likesPer5s, " \u0441\u0435\u0440\u0434\u0435\u0447\u0435\u043A \u0437\u0430 5 \u0441"] }), cell?.safety ? (_jsxs(_Fragment, { children: [_jsx("dt", { className: "text-[var(--color-text-muted)]", children: "\u0417\u0430\u0449\u0438\u0442\u0430 \u0430\u043A\u043A\u0430\u0443\u043D\u0442\u0430" }), _jsxs("dd", { children: [_jsx(AccountSafety, { cell: cell, canManage: canManage, canManageConnections: canManageConnections, items: items }), !cell.safety.blockedAt &&
                                    !cell.safety.stoppedAt &&
                                    cell.safety.status !== 'mode_off' &&
                                    !pauseActive(cell.safety) &&
                                    (cell.safety.warmup === null || cell.safety.warmup.factor >= 1) &&
                                    Object.keys(cell.safety.limits).length === 0 ? (_jsx("span", { className: "text-[var(--color-text-muted)]", children: "\u041F\u0430\u0443\u0437 \u0438 \u043E\u0433\u0440\u0430\u043D\u0438\u0447\u0435\u043D\u0438\u0439 \u043D\u0435\u0442" })) : null] })] })) : null] }) }));
}
//# sourceMappingURL=settings.js.map